/**
 * Icon utilities for the website
 * This file centralizes all icon sourcing logic
 */

// List of domains that have problematic favicons
const PROBLEMATIC_DOMAINS = [
  'skillshift.com.au',
  'resolutionsydney.com',
  'ebaygumtreegroup.com',
  'resolutiondubai.com',
  'hummgroup.com'
];

// Domain mapping for entities with non-standard domains or agencies
const DOMAIN_MAPPINGS: Record<string, string> = {
  // Map company slugs or names to their correct domains
  'resolution-sydney': 'resolutionmedia.com',
  'resolution-dubai': 'resolutionmedia.com',
  'ebaygumtreegroup': 'ebay.com.au',
  'hummgroup': 'shophumm.com.au',
  // Add more mappings as needed
};

/**
 * Ensures a domain has the https:// protocol
 * @param domain The domain name
 * @returns Domain with https:// protocol
 */
export const ensureHttpsProtocol = (domain: string): string => {
  if (!domain) return '';
  
  // If the domain already has a protocol, make sure it's https
  if (domain.startsWith('http:')) {
    return domain.replace('http:', 'https:');
  }
  
  // If no protocol is present, add https://
  if (!domain.startsWith('https:')) {
    return `https://${domain}`;
  }
  
  return domain;
};

/**
 * Prepares a domain for use with Google's favicon service
 * This function strips the protocol from the domain as Google's favicon service
 * handles domains better when protocol is not included
 * @param domain The domain name (with or without protocol)
 * @returns Domain prepared for Google favicon service
 */
export const prepareGoogleFaviconDomain = (domain: string): string => {
  if (!domain) return '';
  
  // Check if we have a special mapping for this domain
  const slug = domain.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  if (DOMAIN_MAPPINGS[slug]) {
    return DOMAIN_MAPPINGS[slug];
  }
  
  // Handle company/agency names that might be passed instead of domains
  if (domain.toLowerCase().includes('resolution sydney')) {
    return 'resolutionmedia.com';
  }
  
  if (domain.toLowerCase().includes('resolution dubai')) {
    return 'resolutionmedia.com';
  }
  
  if (domain.toLowerCase().includes('ebay') && domain.toLowerCase().includes('gumtree')) {
    return 'ebay.com.au';
  }
  
  if (domain.toLowerCase().includes('humm') && !domain.includes('.')) {
    return 'shophumm.com.au';
  }
  
  // Strip any protocol
  let cleanDomain = domain.replace(/^https?:\/\//, '');
  
  // Remove any trailing paths or query parameters
  cleanDomain = cleanDomain.split('/')[0];
  
  return cleanDomain;
};

/**
 * Get the Google favicon URL using the newer faviconV2 API
 * @param domain The domain name
 * @returns URL for the favicon from Google's newer API
 */
export const getGoogleFaviconUrl = (domain: string): string => {
  if (!domain) return '';
  
  const cleanDomain = prepareGoogleFaviconDomain(domain);
  return `https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${cleanDomain}&size=128`;
};

/**
 * Get a brand logo URL with multiple fallback options
 * @param brand The brand object from Contentful
 * @returns URL for the brand logo
 */
export const getBrandLogoUrl = (brand: any): string => {
  // 1. First priority: Use direct Contentful logo if available
  if (brand?.fields?.logo?.fields?.file?.url) {
    return brand.fields.logo.fields.file.url;
  }
  
  // Skip problematic domains
  if (brand?.fields?.domain && PROBLEMATIC_DOMAINS.includes(prepareGoogleFaviconDomain(brand.fields.domain))) {
    // Default placeholder image for problematic domains
    return '/favicon.ico'; // Using your default favicon as a fallback
  }

  // 2. Second priority: Use clearbit logo API if domain is available
  if (brand?.fields?.domain) {
    // Clearbit Logo API (higher quality)
    return `https://logo.clearbit.com/${brand.fields.domain}?size=128`;
    
    // Alternative: Google Favicon (using newer API with better fallback handling)
    // return getGoogleFaviconUrl(brand.fields.domain);
  }
  
  // 3. Third priority: Try a custom image in your public folder
  const slug = brand?.fields?.slug || '';
  return `/brands/${slug.replace(/[^a-z0-9]/g, '')}.svg`;
};

/**
 * Get a technology logo URL with multiple fallback options
 * @param technology The technology object from Contentful
 * @returns URL for the technology logo
 */
export const getTechnologyLogoUrl = (technology: any): string => {
  // 1. First priority: Use direct Contentful logo if available
  if (technology?.logo?.fields?.file?.url) {
    return technology.logo.fields.file.url;
  }
  
  // 2. Second priority: Use DevIcon CDN
  const slug = technology?.slug || '';
  
  // You can customize which DevIcon style to use: 
  // - original, original-wordmark, plain, plain-wordmark, etc.
  const iconStyle = 'original'; 
  
  return `https://cdn.jsdelivr.net/gh/devicons/devicon/icons/${slug}/${slug}-${iconStyle}.svg`;
  
  // 3. Alternative: Use Simple Icons
  // return `https://cdn.simpleicons.org/${slug}`;
};

/**
 * Get a company logo URL with multiple fallback options
 * @param company The company name
 * @param relatedBrands Optional array of related brands that might contain the logo
 * @returns URL for the company logo
 */
export const getCompanyLogoUrl = (company: string, relatedBrands: any[] = []): string => {
  // 1. First priority: Try to find a matching brand logo
  const matchingBrand = relatedBrands.find(brand => 
    brand?.fields?.name?.toLowerCase().includes(company.toLowerCase()) ||
    company.toLowerCase().includes(brand?.fields?.name?.toLowerCase())
  );
  
  if (matchingBrand) {
    return getBrandLogoUrl(matchingBrand);
  }
  
  // 2. Second priority: Try to determine the actual domain name
  const companySlug = company.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
  
  // Array of common domains to try in order of likelihood
  const domainsToTry = [
    // Common international domains
    `${companySlug}.com`,
    `${companySlug}.co`,
    `${companySlug}.org`,
    `${companySlug}.net`,
    
    // Regional domains (especially for Australian companies)
    `${companySlug}.com.au`,
    `${companySlug}.co.uk`,
    
    // Alternate slug formats - some companies use 'get' prefix
    `get${companySlug}.com`,
    
    // Try with hyphens for multi-word company names
    company.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') + '.com',
    
    // Try just the first word if it's a multi-word company
    company.toLowerCase().split(' ')[0] + '.com'
  ];
  
  // Use an alternate logo service that handles fallbacks better
  // Clearbit typically has better quality and handles domain resolution
  return `https://logo.clearbit.com/${domainsToTry[0]}?size=128&fallback=false`;
  
  // Alternative: If you prefer to stick with Google Favicons but want to try multiple domains:
  // You can uncomment this and comment out the clearbit line above
  // return `https://www.google.com/s2/favicons?domain=${prepareGoogleFaviconDomain(domainsToTry[0])}&sz=128`;
}